import { Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { ComplianceBreadcrumbs } from "../components/ComplianceBreadcrumbs";
import { Space } from "~/lib/types";
import {
  ExceptionType,
  GetComplianceControlQuery,
  useGetComplianceControlQuery,
  useGetComplianceFrameworkQuery,
} from "~/operations";
import { FrameworkHeader } from "~/pages/compliance/Framework/FrameworkHeader/FrameworkHeader";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { CompletionV2 } from "~/components/Completion/CompletionV2";
import { setDocumentTitle } from "~/lib/commonUtils";
import { SpeechBubbleIcon } from "~/icons";

export type ComplianceControlData =
  GetComplianceControlQuery["complianceControl"];

type ControlContextType = {
  space: Space;
  control: ComplianceControlData;
  controlMrn: string;
  frameworkMrn: string;
  frameworkName: string;
};

export type ComplianceControlProps = {
  space: Space;
};

export function ComplianceControl({ space }: ComplianceControlProps) {
  const [searchParams] = useSearchParams();
  const frameworkMrn = searchParams.get("frameworkMrn") || "";
  const controlMrn = searchParams.get("controlMrn") || "";

  const {
    data: { complianceControl: control } = {},
    error,
    loading,
  } = useGetComplianceControlQuery({
    variables: {
      input: {
        controlMrn,
        frameworkMrn,
        scopeMrn: space.mrn,
      },
    },
  });

  const { data: { complianceFramework: framework } = {} } =
    useGetComplianceFrameworkQuery({
      variables: {
        input: {
          scopeMrn: space.mrn,
          frameworkMrn: frameworkMrn,
        },
      },
    });

  const { pendingAndReviewedExceptionGroups } = useFetchExceptions({
    mrn: controlMrn,
    scopeMrn: space.mrn,
    types: [ExceptionType.Compliance],
  });

  const { pendingExceptionsGroups: pendingChecksExceptionGroups } =
    useFetchExceptions({
      mrn: controlMrn,
      scopeMrn: space.mrn,
      types: [ExceptionType.Security],
    });

  if (loading) {
    return <LoadingPage what="compliance control" />;
  }

  if (error || !control) {
    return <LoadingFailedPage what="compliance control" />;
  }

  const tabList: TabListItem[] = [
    {
      label: "Findings",
      to: `checks?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&controlMrn=${controlMrn}`,
      route: "/checks",
    },
    {
      label: "Data queries",
      to: `data-queries?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&controlMrn=${controlMrn}`,
      route: "/data-queries",
    },
    {
      label: "Assets",
      to: `assets?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&controlMrn=${controlMrn}`,
      route: "/assets",
    },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          Exceptions
          {pendingChecksExceptionGroups.length > 0 && (
            <Tooltip title="Exceptions pending review" placement="top" arrow>
              <SpeechBubbleIcon color="warning" sx={{ ml: 1, fontSize: 16 }} />
            </Tooltip>
          )}
        </Box>
      ),
      to: `exceptions?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&controlMrn=${controlMrn}`,
      route: "/exceptions",
    },
  ].flatMap((tab) => tab ?? []);

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "checks",
  );

  setDocumentTitle([control.title, framework?.name, "Compliance", space.name]);

  return (
    <Box>
      <ComplianceBreadcrumbs
        space={space}
        framework={framework}
        control={control}
      />
      <FrameworkHeader
        space={space}
        control={control}
        exceptionGroups={pendingAndReviewedExceptionGroups}
        right={
          <CompletionV2
            progress={control.completion}
            exceptionCount={control.checksStats.totalExceptions}
          />
        }
      />
      <TabNavigation {...{ id: "space-settings-tabs", tabList, currentTab }} />
      <Box>
        <Outlet
          context={
            {
              space,
              controlMrn,
              frameworkName: framework?.name || "",
              frameworkMrn,
              control,
            } satisfies ControlContextType
          }
        />
      </Box>
    </Box>
  );
}

export function useControlContext() {
  return useOutletContext<ControlContextType>();
}
